import React from 'react';
import PropTypes from 'prop-types';
import { prop, assoc } from 'ramda';
import moment from 'moment';

import { Document, Page, View, Text } from '@react-pdf/renderer';
import Header from '../Header';

import { convertToReal } from 'helpers/converters';
import { formatServicesCategories } from 'helpers/formatters';

import { styles } from '../styles';

const col5 = { width: `${100 / 5}%` };

const getPercentage = (total, quantity) => {
  if(!quantity) {
    return '0 %';
  }

  return `${Number(quantity * 100 / total).toFixed(2)} %`;
}

const Footer = ({ total, amount, totalPeriod, text = '', style }) => {
  const amountTotal = amount || amount == 0 ? `Total: ${convertToReal(amount)}` : '';
  const periodTotal = totalPeriod && totalPeriod !== '0' ? ` / Período total: ${moment.duration(parseInt(totalPeriod, 10), 'seconds').format('D [dia(s)], H [h e] m [min]')}` : '';

  return(
    <View style={[styles.footer, style, { display: 'flex', flexDirection: 'column', backgroundColor: '#022A5C' }]}>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        {!!total && (
          <Text style={[styles.text, { color: '#FFFFFF', marginRight: 5 }]}>
            {(+total > 1) && `Este relatório tem ${total} itens.`}
            {(+total == 1) && `Este relatório tem ${total} item.`}
          </Text>
        )}
        {!!amountTotal && (
          <Text style={[styles.text, styles.label, { color: '#FFFFFF' }]}>
            {amountTotal}
          </Text>
        )}
      </View>
      {!!periodTotal && (
        <Text style={[styles.text, { color: '#FFFFFF' }]}>
          {periodTotal}
        </Text>
      )}
      {!!text && (
        <Text style={[styles.text, styles.label, { color: '#FFFFFF' }]}>
          {text}
        </Text>
      )}
    </View>
  );
}

const CashFlow = ({ cashFlow }) => {
  return(
    <View style={{ marginBottom: 10 }}>
      <View>
        <Text style={[styles.financialResumeTitle, { marginTop: 10, marginBottom: 10 }]}>
          Fluxo de caixa
        </Text>
      </View>
      <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <View style={styles.financialResumeSuccess}>
          <Text style={styles.financialResumeBoxTitle}>
            Receita
          </Text>
          <Text style={styles.financialResumeBoxValue}>
            {convertToReal(+cashFlow.entry)}
          </Text>
        </View>
        <View style={styles.financialResumeDanger}>
          <Text style={styles.financialResumeBoxTitle}>
            Despesa
          </Text>
          <Text style={styles.financialResumeBoxValue}>
            {convertToReal(+cashFlow.exit)}
          </Text>
        </View>
        <View style={styles.financialResumePrimary}>
          <Text style={styles.financialResumeBoxTitle}>
            Lucro
          </Text>
          <Text style={styles.financialResumeBoxValue}>
            {convertToReal(+cashFlow.balance)}
          </Text>
        </View>
      </View>
    </View>
  );
}

const Resume = ({ data, name, type, isProduct = false, isProductOs = false, isDiscount = false, isServices = false }) => {
  if(!data) {
    return null;
  }

  const sumTotal = data?.content?.reduce((total, item) => total + +item?.total, 0);
  const sumAmount = data?.content?.reduce((total, item) => total + +item?.totalAmount, 0);

  return(
    <View>
      <View>
        <Text style={[styles.financialResumeTitle, { marginBottom: 5 }]}>
          {name}
        </Text>
      </View>
      <View style={styles.tableFinancial}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantidade</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantitativo (%)</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Faturamento (%)</Text>
          </View>
        </View>
        {data.content.map(resume => (
          <View style={styles.tableRowFinancial}>
            <View style={[styles.tableColFinancial, col5]}>
              {(isProduct || isProductOs) ? (
                <Text style={styles.tableCellFinancial}>
                  {resume?.name}
                </Text>
              ) : (
                <>
                  {isServices ? (
                    <View>
                      <Text style={[styles.tableCellFinancial, { marginBottom: 0 }]}>
                        {resume?.description}
                      </Text>
                      {(resume?.categories != '-') && (
                        <Text style={[styles.tableCellFinancial, styles.fontSize6]}>
                          Categoria(s): {resume?.categories}
                        </Text>
                      )}
                    </View>
                  ) : (
                    <Text style={styles.tableCellFinancial}>{prop(type, resume)}</Text>
                  )}
                </>
              )}
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {convertToReal(+resume?.totalAmount || 0)}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {isProductOs ? parseInt(resume?.total || 0) : resume?.total || 0}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {getPercentage(sumTotal, resume?.total)}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {getPercentage(sumAmount, resume?.totalAmount)}
              </Text>
            </View>
          </View>
        ))}
        {isDiscount ? (
          <Footer
            style={{ borderStyle: 'solid 1px', borderColor: '#D0D0D0', borderWidth: 1 }}
            total={data.total}
            amount={data.totalAmount}
            text='O valor total da receita acima é resultado da soma dos serviços abaixo menos o valor total do desconto.'
          />
        ) : (
          <Footer
            style={{ borderStyle: 'solid 1px', borderColor: '#D0D0D0', borderWidth: 1 }}
            total={data.total}
            amount={data.totalAmount}
          />
        )}
      </View>
    </View>
  );
}

const PaymentMethods = ({ data, name }) => {
  const sumQuantity = data?.content?.reduce((total, item) => total + +item?.total, 0);
  const sumAmount = data?.content?.reduce((total, item) => total + +item?.totalAmount, 0);

  return(
    <View>
      <View>
        <Text style={[styles.financialResumeTitle, { marginBottom: 5 }]}>
          {name}
        </Text>
      </View>
      <View style={styles.tableFinancial}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantidade</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantitativo (%)</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Faturamento (%)</Text>
          </View>
        </View>
        {data?.content?.map(item => {
          return(
            <View style={styles.tableRowFinancial}>
              <View style={[styles.tableColFinancial, col5]}>
                <Text style={styles.tableCellFinancial}>
                  {item?.paymentMethodName || ''}
                </Text>
              </View>
              <View style={[styles.tableColFinancial, col5]}>
                <Text style={styles.tableCellFinancial}>
                  {convertToReal(item?.totalAmount || '')}
                </Text>
              </View>
              <View style={[styles.tableColFinancial, col5]}>
                <Text style={styles.tableCellFinancial}>
                  {item?.total || ''}
                </Text>
              </View>
              <View style={[styles.tableColFinancial, col5]}>
                <Text style={styles.tableCellFinancial}>
                  {getPercentage(sumQuantity, item?.total)}
                </Text>
              </View>
              <View style={[styles.tableColFinancial, col5]}>
                <Text style={styles.tableCellFinancial}>
                  {getPercentage(sumAmount, item?.totalAmount)}
                </Text>
              </View>
            </View>
          );
        })}
        <Footer
          style={{ borderStyle: 'solid 1px', borderColor: '#D0D0D0', borderWidth: 1 }}
          total={data?.total}
          amount={data?.totalAmount}
        />
      </View>
    </View>
  );
}

const Invoices = ({ data, name }) => {
  const { paidOut } = data?.content || {};
  const invoices = [assoc('label', 'Pagas', paidOut)];

  const sumTotal = invoices?.reduce((total, item) => total + +item?.total, 0);
  const sumAmount = invoices?.reduce((total, item) => total + +item?.totalAmount, 0);

  if(data.total == 0) {
    return false;
  }

  return(
    <View>
      <View>
        <Text style={[styles.financialResumeTitle, { marginBottom: 5 }]}>
          {name}
        </Text>
      </View>
      <View style={styles.tableFinancial}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantidade</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantitativo (%)</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Faturamento (%)</Text>
          </View>
        </View>
        {invoices.map(invoice => invoice.totalAmount != 0 && (
          <View style={styles.tableRowFinancial}>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {invoice.label}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {convertToReal(+invoice.totalAmount)}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {invoice.total}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {getPercentage(sumTotal, invoice?.total)}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, col5]}>
              <Text style={styles.tableCellFinancial}>
                {getPercentage(sumAmount, invoice?.totalAmount)}
              </Text>
            </View>
          </View>
        ))}
        <Footer
          style={{ borderStyle: 'solid 1px', borderColor: '#D0D0D0', borderWidth: 1 }}
          total={data?.total}
          amount={data?.totalAmount}
        />
      </View>
    </View>
  );
}

const FinancialResume = ({
  exportData,
  dateRange,
  fileName,
  establishmentName
}) => {
  const {
    cashFlow,
    serviceOrders,
    services,
    products,
    productsOs,
    reservations,
    entryCategory,
    exitCategory,
    invoices,
    paymentMethods,
    discounts,
    servicesCategories
  } = exportData || {};

  const servicesWithCategories = services?.content?.map(service => {
    return {
      ...service,
      categories: formatServicesCategories(service?.serviceCategories, servicesCategories)
    }
  });

  const formattedServices = {
    ...services,
    content: servicesWithCategories
  }

  return(
    <Document>
      <Page style={styles.body}>
        <Header
          fileName={fileName}
          dateRange={dateRange}
          establishmentName={establishmentName}
        />
        <CashFlow cashFlow={cashFlow} />
        {(+discounts?.totalAmount != 0) && (
          <Resume
            isDiscount
            name="Descontos"
            type="description"
            data={discounts}
          />
        )}
        {(serviceOrders?.content?.length > 0) && (
          <Resume
            name="Rotativos"
            type="typePrice"
            data={serviceOrders}
          />
        )}
        {(services?.content?.length > 0) && (
          <Resume
            name="Serviços"
            type="description"
            data={formattedServices}
            isServices
          />
        )}
        {(products?.content?.length > 0) && (
          <Resume
            name="Produtos"
            type="description"
            data={products}
            isProduct
          />
        )}
        {(productsOs?.content?.length > 0) && (
          <Resume
            name="Produtos (OS)"
            type="description"
            data={productsOs}
            isProductOs
          />
        )}
        {(reservations?.content?.length > 0) && (
          <Resume
            name="Reservas"
            type="reservationTypeName"
            data={reservations}
          />
        )}
        {(entryCategory?.content?.length > 0) && (
          <Resume
            name="Movimentações de entrada"
            type="categoryName"
            data={entryCategory}
          />
        )}
        {(exitCategory?.content?.length > 0) && (
          <Resume
            name="Movimentações de saída"
            type="categoryName"
            data={exitCategory}
          />
        )}
        {(invoices?.total > 0) && (
          <Invoices
            data={invoices}
            name="Faturas"
          />
        )}
        {(paymentMethods?.content?.length > 0) && (
          <PaymentMethods
            data={paymentMethods}
            name="Meios de pagamento"
          />
        )}
      </Page>
    </Document>
  );
}

FinancialResume.propTypes = {
  exportData: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  fileName: PropTypes.string.isRequired,
  establishmentName: PropTypes.string.isRequired
};

CashFlow.propTypes = {
  cashFlow: PropTypes.shape({
    entry: PropTypes.oneOf([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired
    ]).isRequired,
    exit: PropTypes.oneOf([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired
    ]).isRequired,
    balance: PropTypes.oneOf([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired
    ]).isRequired
  }).isRequired
};

Resume.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired
};

PaymentMethods.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  name: PropTypes.string.isRequired
};

Invoices.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  name: PropTypes.string.isRequired
};

export default FinancialResume;